export type CriteriaType =
  | 'user_timespent_event'
  | 'poll_responses'
  | 'qna_answered'
  | 'in_app_survey'
  | 'resources_downloaded'
  | 'cta_clicked';

export type RequirementType = 'at_least' | 'exactly';

export interface Criteria {
  type: CriteriaType | '';
  requirement: RequirementType;
  value: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface Session {
  id: string;
  name: string;
}

export interface Rule {
  criteria: Criteria[];
  creditAmount: string;
  appliesToEvent: boolean;
  appliesToSessions: boolean;
  sessions: string[];
}

export interface RuleSetFormData {
  rules: Rule[];
  certificate: string;
  emailDesign: string;
}

export interface Certificate {
  id: string;
  name: string;
  createdDate: string;
}

export enum CreditRuleRequestFor {
  Event = 'event',
  Template = 'template',
  None = ''
}

export interface StepNavItem {
  label: string;
}

export type EmailDeliveryStatus = 'PENDING' | 'SENT' | 'FAILED';

export type AuditLogScope = 'event' | 'sessions';

export interface AuditLogEntry {
  attendee_id: string;
  name: string;
  email: string;
  event_id: string;
  event_name?: string;
  session_id?: string;
  session_name?: string;
  date: string;
  cpe_credited: number;
  pdf_url?: string;
  status?: EmailDeliveryStatus;
  qualified: boolean;
}

export interface AuditLogPagination {
  page: number;
  page_size: number;
  total: number;
}

export interface AuditLogResponse {
  data: AuditLogEntry[];
  pagination: AuditLogPagination;
}

export interface ResendCertificateParams {
  attendee_email: string;
  event_id: string;
  session_id?: string;
}

export interface ResendCertificateResponse {
  queued: number;
  failed: number;
  total: number;
}

export interface FetchAuditLogParams {
  eventId?: string;
  sessionId?: string;
  scope?: AuditLogScope;
  qualified?: boolean;
  email?: string;
  page?: number;
  pageSize?: number;
}

export const AUDIT_LOG_PAGE_SIZE = 25;

export type CertificatesUsedTimeRange = 'this_month' | 'last_3_months' | 'last_6_months' | 'this_year' | 'total';

export interface CertificatesUsedResponse {
  org_id: string;
  certificates_used: number;
  /** Month-over-month trend: (this_month − last_month) / last_month × 100. Independent of time_range. */
  percentage_increase: number;
}

export interface FetchCertificatesUsedParams {
  orgId: string;
  timeRange?: CertificatesUsedTimeRange;
}

export const DELIVERY_STATUS_LABELS: Record<EmailDeliveryStatus, string> = {
  PENDING: 'Pending',
  SENT: 'Sent',
  FAILED: 'Failed'
};

export function deliveryStatusLabel(status?: EmailDeliveryStatus): string {
  return status ? DELIVERY_STATUS_LABELS[status] : 'Not sent';
}

export function deliveryStatusVariation(status?: EmailDeliveryStatus): 'green' | 'orange' | 'red' | 'gray' {
  switch (status) {
    case 'SENT':
      return 'green';
    case 'PENDING':
      return 'orange';
    case 'FAILED':
      return 'red';
    default:
      return 'gray';
  }
}

export function creditLabel(credit: number): string {
  return `${credit} CPE`;
}

export function createDefaultCriteria(): Criteria {
  return { type: '', requirement: 'at_least', value: '' };
}

export function createDefaultRule(): Rule {
  return {
    criteria: [createDefaultCriteria()],
    creditAmount: '',
    appliesToEvent: false,
    appliesToSessions: false,
    sessions: []
  };
}

export const CRITERIA_LABELS: Record<CriteriaType, string> = {
  user_timespent_event: 'Live Time Spent',
  poll_responses: 'Polls Answered',
  qna_answered: 'Questions Asked',
  in_app_survey: 'Surveys Submitted',
  resources_downloaded: 'Resources Downloaded',
  cta_clicked: 'CTA Clicked'
};

export const REQUIREMENT_LABELS: Record<RequirementType, string> = {
  at_least: 'At least',
  exactly: 'Exactly'
};

export function criteriaLabel(type: CriteriaType | ''): string {
  return type ? CRITERIA_LABELS[type] : '';
}

export function requirementLabel(requirement: RequirementType): string {
  return REQUIREMENT_LABELS[requirement];
}

export function criteriaValueLabel(type: CriteriaType | '', value: string): string {
  if (type === 'user_timespent_event') {
    return `${value} min`;
  }
  return value;
}

export function emailDesignNameLabel(emailDesign: string, templates: { id: string; name: string }[]): string {
  return templates.find(t => t.id === emailDesign)?.name || emailDesign;
}

export function ruleCriteriaInline(rule: RuleSetFormData['rules'][number]): string {
  return rule.criteria
    .filter(c => c.type)
    .map(c => `${criteriaLabel(c.type)}: ${requirementLabel(c.requirement)} ${criteriaValueLabel(c.type, c.value)}`)
    .join(', ');
}
